// Conversaciones de los buzones que ve esta persona, por paginas (la lista de Correos y el contador del menu).
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type HiloLista, hilosDeBuzones, type VistaServidor } from '../../api/correo';
import { useSession } from '../../auth/session';
import { buzonesVisibles } from '../../lib/buzonesVisibles';
import { guardarCache, leerCache } from '../../lib/cacheLocal';
import { useMiembros } from '../../data/queries';
import { useBuzones } from '../../ui/Buzones';
import { useBuzonesGrupo } from '../administracion/BuzonesGrupo';

// Buzones que esta persona ve, con la misma regla que aplica el servicio de correo (D38, D83): los personales suyos y
// los de grupo donde es miembro. Ser administrador no da acceso al correo de otros.
export function useBuzonesVisibles() {
  const { yo } = useSession();
  const todas = useBuzones();
  const grupos = useBuzonesGrupo();
  const equipo = useMiembros();
  return useMemo(() => {
    const cuentas = todas.data;
    if (!cuentas) return { ...todas, grupos };
    // Sin la lista del equipo no se sabe que buzon es personal: se espera en vez de mostrarlos todos.
    const data = equipo.data ? buzonesVisibles(cuentas, grupos.data ?? [], equipo.data, yo.miembroId) : [];
    return { ...todas, data, isPending: !equipo.data && equipo.isPending, grupos };
  }, [todas, grupos, equipo.data, yo.miembroId]);
}

type Pagina = { hilos: HiloLista[]; siguiente?: string };

export function useHilos(cuentas: string[], vista: VistaServidor, q = '') {
  const clave = `hilos.${vista}.${cuentas.join(',')}`;
  // La primera pagina (sin busqueda) se guarda en el navegador para que al refrescar se vea al instante. Se lee y se
  // arma una sola vez por buzones/vista (antes se leia y parseaba de localStorage en cada render).
  const inicial = useMemo(() => {
    const guardada = !q ? leerCache<Pagina>(clave) : undefined;
    return guardada ? { initialData: { pages: [{ ...guardada.data, hilos: guardada.data.hilos.map((h) => ({ ...h, mensajes: [h.ultimo] })) }], pageParams: [undefined] }, initialDataUpdatedAt: guardada.en } : {};
  }, [clave, q]);
  const r = useInfiniteQuery({
    queryKey: ['correos', 'hilos', vista, cuentas.join(','), q],
    queryFn: async ({ pageParam }) => {
      const p = await hilosDeBuzones(cuentas, { vista, antes: pageParam, q: q || undefined });
      if (!pageParam && !q) guardarCache(clave, { ...p, hilos: p.hilos.slice(0, 60).map((h) => ({ ...h, ultimo: { ...h.ultimo, texto: h.ultimo.texto.slice(0, 300) }, mensajes: [] })) });
      return p;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p: Pagina) => p.siguiente,
    enabled: cuentas.length > 0,
    // Cada 2 min (antes cada minuto): refresca todas las paginas cargadas de cada buzon.
    refetchInterval: 2 * 60_000,
    staleTime: 20_000,
    ...inicial,
  });
  const hilos = useMemo(() => {
    const vistos = new Set<string>();
    return (r.data?.pages ?? []).flatMap((p) => p.hilos).filter((h) => !vistos.has(h.id) && !!vistos.add(h.id));
  }, [r.data]);
  return { ...r, hilos };
}
